import React from 'react'
import { usePodcastEditor } from '../../context/use-podcast-editor'
import { useEditorStore } from '../../state/editor-store'
import { useScriptSuggestionsStore } from '../../state/script-suggestions-store'
import type { ScriptSuggestionChange } from '../../../../types/script-refinement'
import { ScriptErrorState, EmptyDraftState, WaitingForScriptState } from '../empty-states'
import { EditorFooter } from '../footer'
import { useEditorActions } from './editor-actions'
import { useHostConfiguration } from './host-configuration'
import { usePodcastLines } from './podcast-lines-manager'
import { PodcastLinesList } from './podcast-lines-list'
import { useEditorState } from './editor-state-manager'
import { applySuggestionToScript, dialogueToBulkPayload } from '../../utils/script-suggestions'
import { overwritePodcastScript } from '../../../../api/podcasts'
import { useToast } from '../../../../components/ui/toast-provider'
import { ScriptSuggestionsBar } from './suggestions/script-suggestions-bar'

export const PodcastLinesEditor: React.FC<{
  onOpenSettings?: () => void
  settingsOpen?: boolean
  onOpenChat?: () => void
  chatOpen?: boolean
}> = ({ onOpenSettings, settingsOpen, onOpenChat, chatOpen }) => {
  const { podcast, voices, hostAVoiceId, hostBVoiceId, setLineText, refetchAll, deleteLine, addLineBelow } = usePodcastEditor()
  const { currentLines } = usePodcastLines(podcast)
  const activity = useEditorStore((s) => s.activity)
  const scriptJobStatus = useEditorStore((s) => s.scriptJobStatus)
  const scriptErrorMessage = useEditorStore((s) => s.scriptErrorMessage)
  const { addToast } = useToast()
  const changes = useScriptSuggestionsStore((s) => s.changes)
  const metadata = useScriptSuggestionsStore((s) => s.metadata)
  const activeChangeId = useScriptSuggestionsStore((s) => s.activeChangeId)
  const proposedDialogue = useScriptSuggestionsStore((s) => s.proposedDialogue)
  const suggestionsPodcastId = useScriptSuggestionsStore((s) => s.podcastId)
  const setActiveSuggestion = useScriptSuggestionsStore((s) => s.setActive)
  const markAcceptedSuggestion = useScriptSuggestionsStore((s) => s.markAccepted)
  const markRejectedSuggestion = useScriptSuggestionsStore((s) => s.markRejected)
  const clearSuggestions = useScriptSuggestionsStore((s) => s.clear)
  const jumpSuggestion = useScriptSuggestionsStore((s) => s.jump)

  const [applyingSuggestionId, setApplyingSuggestionId] = React.useState<string | null>(null)
  const [isApplyingAll, setIsApplyingAll] = React.useState(false)

  // Detect query param showLoader (string 'true')
  const showLoader = React.useMemo(() => {
    if (typeof window === 'undefined') return false
    const params = new URLSearchParams(window.location.search)
    return params.get('showLoader') === 'true'
  }, [])

  React.useEffect(() => {
    if (showLoader) {
      const interval = setInterval(() => {
        void refetchAll()
      }, 1000)
      const timeout = setTimeout(() => clearInterval(interval), 5000)
      return () => {
        clearInterval(interval)
        clearTimeout(timeout)
      }
    }
  }, [showLoader, refetchAll])

  React.useEffect(() => {
    if (!podcast?.id) return
    if (suggestionsPodcastId && suggestionsPodcastId !== podcast.id) {
      // Clear the previous podcast's suggestions, not the current one
      clearSuggestions(suggestionsPodcastId)
    }
  }, [podcast?.id, suggestionsPodcastId, clearSuggestions])

  // Ensure we scroll to the first/active suggestion after it renders.
  // Use a short retry window in case the DOM hasn't painted the suggestion cards yet.
  React.useEffect(() => {
    if (!activeChangeId) return
    let attempts = 0
    const maxAttempts = 15
    const tryScroll = () => {
      const el = document.querySelector<HTMLElement>(`[data-suggestion-id="${activeChangeId}"]`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return true
      }
      return false
    }
    // First try on next frame
    let raf = requestAnimationFrame(() => {
      if (tryScroll()) return
      const interval = setInterval(() => {
        attempts += 1
        if (tryScroll() || attempts >= maxAttempts) {
          clearInterval(interval)
        }
      }, 100)
    })
    return () => {
      cancelAnimationFrame(raf)
    }
  }, [activeChangeId])

  const currentDialogue = React.useMemo(() => {
    return currentLines.map((line) => ({
      lineId: line.id,
      speaker: (line.host === 'A' ? 'A' : 'B') as 'A' | 'B',
      line: line.text,
    }))
  }, [currentLines])

  const suggestionGroups = React.useMemo(() => {
    const lineIdSet = new Set(currentLines.map((line) => line.id))
    const byLine: Record<string, ScriptSuggestionChange[]> = {}
    const beforeMap: Record<string, ScriptSuggestionChange[]> = {}
    const afterMap: Record<string, ScriptSuggestionChange[]> = {}
    const start: ScriptSuggestionChange[] = []
    const end: ScriptSuggestionChange[] = []

    const addToMap = (map: Record<string, ScriptSuggestionChange[]>, key: string, change: ScriptSuggestionChange) => {
      if (!map[key]) map[key] = []
      map[key].push(change)
    }

    changes.forEach((change) => {
      if ((change.kind === 'modify' || change.kind === 'delete') && change.targetLineId) {
        addToMap(byLine, change.targetLineId, change)
        return
      }
      if (change.kind === 'insert') {
        if (change.beforeLineId && lineIdSet.has(change.beforeLineId)) {
          addToMap(beforeMap, change.beforeLineId, change)
        } else if (change.afterLineId && lineIdSet.has(change.afterLineId)) {
          addToMap(afterMap, change.afterLineId, change)
        } else if (currentLines.length === 0) {
          start.push(change)
        } else {
          end.push(change)
        }
      }
    })

    const sortList = (list: ScriptSuggestionChange[]) => [...list].sort((a, b) => a.targetIndex - b.targetIndex)
    const sortMap = (map: Record<string, ScriptSuggestionChange[]>) => {
      const sorted: Record<string, ScriptSuggestionChange[]> = {}
      Object.entries(map).forEach(([key, value]) => {
        sorted[key] = sortList(value)
      })
      return sorted
    }

    return {
      suggestionsByLine: sortMap(byLine),
      insertBeforeMap: sortMap(beforeMap),
      insertAfterMap: sortMap(afterMap),
      insertAtStart: sortList(start),
      insertAtEnd: sortList(end),
    }
  }, [changes, currentLines])

  const activeIndex = React.useMemo(() => {
    if (!activeChangeId) return -1
    return changes.findIndex((change) => change.id === activeChangeId)
  }, [changes, activeChangeId])

  const suggestionsDisabled = scriptJobStatus === 'running' || applyingSuggestionId !== null || isApplyingAll

  const handleFocusSuggestion = React.useCallback((suggestion: ScriptSuggestionChange) => {
    setActiveSuggestion(suggestion.id)
  }, [setActiveSuggestion])

  const handleRejectSuggestion = React.useCallback((suggestion: ScriptSuggestionChange) => {
    markRejectedSuggestion(suggestion.id)
  }, [markRejectedSuggestion])

  const handleAcceptSuggestion = React.useCallback(async (suggestion: ScriptSuggestionChange) => {
    if (!podcast?.id) return
    setApplyingSuggestionId(suggestion.id)
    try {
      const nextDialogue = applySuggestionToScript(currentDialogue, suggestion)
      await overwritePodcastScript(podcast.id, { lines: dialogueToBulkPayload(nextDialogue) })
      markAcceptedSuggestion(suggestion.id)
      await refetchAll()
    } catch (error) {
      console.error('Failed to apply suggestion', error)
      addToast({
        title: 'Could not apply suggestion',
        description: error instanceof Error ? error.message : 'Try refreshing and requesting suggestions again.',
        variant: 'error',
      })
    } finally {
      setApplyingSuggestionId(null)
    }
  }, [podcast?.id, currentDialogue, addToast, markAcceptedSuggestion, refetchAll])

  const handleAcceptAll = React.useCallback(async () => {
    if (!podcast?.id) return
    if (!proposedDialogue || proposedDialogue.length === 0) {
      addToast({
        title: 'Nothing to apply',
        description: 'Generate suggestions first to accept all changes.',
        variant: 'info',
      })
      return
    }
    setIsApplyingAll(true)
    try {
      await overwritePodcastScript(podcast.id, { lines: dialogueToBulkPayload(proposedDialogue) })
      clearSuggestions(podcast.id)
      await refetchAll()
    } catch (error) {
      console.error('Failed to apply all suggestions', error)
      addToast({
        title: 'Could not apply all changes',
        description: error instanceof Error ? error.message : 'Try again after refreshing the editor.',
        variant: 'error',
      })
    } finally {
      setIsApplyingAll(false)
    }
  }, [podcast?.id, proposedDialogue, addToast, clearSuggestions, refetchAll])

  const handleRejectAll = React.useCallback(() => {
    clearSuggestions(podcast?.id)
  }, [clearSuggestions, podcast?.id])

  const handlePrevSuggestion = React.useCallback(() => {
    jumpSuggestion('prev')
  }, [jumpSuggestion])

  const handleNextSuggestion = React.useCallback(() => {
    jumpSuggestion('next')
  }, [jumpSuggestion])

  // Use extracted hooks and components
  const { hostLabels, hostColors } = useHostConfiguration(voices, hostAVoiceId, hostBVoiceId)
  const { actionLoading, smartExport } = useEditorActions(podcast?.id)
  const { shouldShowSkeleton, shouldShowError, shouldShowEmptyDraft } = useEditorState(
    podcast,
    scriptJobStatus,
    currentLines.length
  )

  // New conditional: waiting state before any backend progress when launched from AI starting point
  const shouldShowNewMessage = showLoader && currentLines.length === 0 && scriptJobStatus === 'idle' && !activity

  const handleLineTextInput = React.useCallback((lineId: string, text: string) => {
    setLineText(lineId, text)
  }, [setLineText])

  const handleSmartExport = React.useCallback(() => {
    void smartExport()
  }, [smartExport])

  const handleRetryScript = React.useCallback(async () => {
    useEditorStore.getState().setScriptJobStatus('running', null)
    await refetchAll()
  }, [refetchAll])

  const handleAddFirstLine = React.useCallback(async () => {
    if (!podcast?.id) return
    try {
      if (currentLines.length === 0) {
        const { apiRequest } = await import('../../../../api/config')
        await apiRequest(`/api/podcast/${podcast.id}/lines`, { method: 'POST', body: JSON.stringify({ text: '', host: 'A' }) })
      }
      await refetchAll()
    } catch {
      // swallow
    }
  }, [podcast?.id, currentLines.length, refetchAll])

  if (shouldShowError) {
    return (
      <ScriptErrorState 
        errorMessage={scriptErrorMessage}
        onRetry={handleRetryScript}
      />
    )
  }

  // Unified waiting / generating state
  if (shouldShowNewMessage || shouldShowSkeleton) {
    return <WaitingForScriptState activity={activity} />
  }

  if (shouldShowEmptyDraft) {
    return <EmptyDraftState onAddFirstLine={handleAddFirstLine} />
  }

  const { suggestionsByLine, insertBeforeMap, insertAfterMap, insertAtStart, insertAtEnd } = suggestionGroups
  const totalSuggestions = changes.length
  const normalizedActiveIndex = totalSuggestions === 0 ? 0 : Math.max(0, activeIndex)

  return (
    <div className="p-8 space-y-8 pb-0">
      {metadata && (
        <ScriptSuggestionsBar
          metadata={metadata}
          totalSuggestions={totalSuggestions}
          activeIndex={normalizedActiveIndex}
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onPrev={handlePrevSuggestion}
          onNext={handleNextSuggestion}
          isApplyingAll={isApplyingAll || applyingSuggestionId !== null}
          actionsLocked={suggestionsDisabled}
        />
      )}
      <PodcastLinesList
        currentLines={currentLines}
        hostLabels={hostLabels}
        hostColors={hostColors}
        onTextInput={handleLineTextInput}
        onDelete={deleteLine}
        onAddLineBelow={addLineBelow}
        suggestionsByLine={suggestionsByLine}
        insertBeforeMap={insertBeforeMap}
        insertAfterMap={insertAfterMap}
        insertAtStart={insertAtStart}
        insertAtEnd={insertAtEnd}
        activeSuggestionId={activeChangeId}
        onAcceptSuggestion={handleAcceptSuggestion}
        onRejectSuggestion={handleRejectSuggestion}
        onFocusSuggestion={handleFocusSuggestion}
        applyingSuggestionId={applyingSuggestionId}
        suggestionsDisabled={suggestionsDisabled}
      />

      <EditorFooter
        podcast={podcast}
        currentLines={currentLines}
        onSmartExport={handleSmartExport}
        actionLoading={actionLoading}
        onOpenSettings={onOpenSettings}
        settingsOpen={settingsOpen}
        onOpenChat={onOpenChat}
        chatOpen={chatOpen}
      />
    </div>
  )
}